import {Category, FaqListProps} from './types';
import {IProps as LinkProps, LinkCategory, LinkType} from '../Links/types';
import Filter from './Filter';
import LinkBadge from '../Links/LinkBadge';
import React, {useEffect, useState} from 'react';
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {getLocale} from "@apps/Shared/Utils/Utils";
import {useGetFaqCategoriesQuery} from "@apps/Shared/GraphQL/generated";
import './Scss/faq-list.scss';

function getNonEmptyCategories(categories: Category[]) {
    return categories.filter(category => category.links.length > 0);
}

function normalize(input: string) {
    return input
        .toLowerCase()
        .split(' ')
        .join('');
}

function getFilteredLinks(links: LinkProps[], searchVal: string) {
    searchVal = normalize(searchVal);

    if (searchVal.length < 3) {
        return links;
    }

    return links.filter((link: LinkProps) => {
        if (normalize(link.title || '').indexOf(searchVal) !== -1) {
            return true;
        }
        if (normalize(link.teaser || '').indexOf(searchVal) !== -1) {
            return true;
        }
        return normalize((link.meta && link.meta.answer) || '').indexOf(searchVal) !== -1;
    })
}

function getCategoriesWithFilteredLinks(
    categories: Category[],
    searchVal: string
) {
    return categories.map((category: Category) => {
        return {
            ...category,
            links: getFilteredLinks(category.links, searchVal),
        };
    });
}

function getFilteredCategories(
    categories: Category[],
    selectedCategoryId: number | null
) {
    if (selectedCategoryId === null) {
        return categories;
    } else {
        return categories.filter(
            (category: Category) => category.id === selectedCategoryId);
    }
}

const FaqList = (props: FaqListProps) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
        null
    );
    const [allCategories, setAllCategories] = useState<Category[]>([]);

    const [searchVal, setSearchVal] = useState<string>('');

    const categories = getNonEmptyCategories(allCategories);
    const categoriesWithFilteredLinks = getCategoriesWithFilteredLinks(
        categories,
        searchVal
    );
    const filteredCategories = getFilteredCategories(
        categoriesWithFilteredLinks,
        selectedCategoryId
    );

    function getCategoryItems(items: any[]): Category[] {
        return items.flatMap(item => {
            const categoryPageModel = item._children?.FaqCategoryPageModel?.items || [];

            return categoryPageModel.map((categoryItem: any) => {
                const categoryName = categoryItem.Name;
                const categoryId = categoryItem.ContentLink?.Id;

                const links: LinkProps[] = categoryItem._children?.FaqItemPageModel?.items.map((faqItem: any) => ({
                    category: LinkCategory.Page,
                    linkType: LinkType.Intern,
                    title: faqItem.Name || '',
                    teaser: faqItem.TeaserText,
                    link: faqItem.RelativePath || '',
                    hideIcon: true,
                })) || [];

                return {
                    id: categoryId,
                    name: categoryName,
                    links: links,
                };
            });
        });
    }

    const locale = getLocale();

    const {data} = useGetFaqCategoriesQuery({
        id: props.id,
        locale: [locale]
    });

    useEffect(() => {
        if (data?.FaqListPageModel?.items) {
            setAllCategories(getCategoryItems(data.FaqListPageModel.items));
        }
    }, [data]);

    return (
        <div className='faq-list'>
            <Container fluid={true}>
                <Row>
                    <Col>
                        <Filter
                            categories={categoriesWithFilteredLinks}
                            searchVal={searchVal}
                            normalizedSearchVal={normalize(searchVal)}
                            onChangeSelectedCategoryId={setSelectedCategoryId.bind(
                                null
                            )}
                            onChangeSearchVal={setSearchVal.bind(null)}
                        />
                    </Col>
                </Row>
                <Row>
                    {filteredCategories.map((category: Category) => {
                        return (
                            <React.Fragment key={category.id}>
                                {category.links.map(
                                    (link: LinkProps) => (
                                        <Col sm={6} key={link.link}>
                                            <div className='faq-list__link-container'>
                                                <LinkBadge {...link} />
                                            </div>
                                        </Col>
                                    )
                                )}
                            </React.Fragment>
                        );
                    })}
                </Row>
            </Container>
        </div>
    );
}

export default FaqList;
